import {
  personalisationApi, realmSettingsApi, ulfShieldExpertsApi, usersApi
} from '@src/api/apiModuleInstances'
import unleashApi from '@src/api/unleash'
import statisticsApi from '@src/api/statistics'
import { ISuperproperties } from '@src/types/superproperties.types'
import { getPermissionLevel, PermissionLevel } from '@src/utilities/userUtils'
import _get from 'lodash/get'
import { isInCrossDomainIframe } from '@src/utilities/iframeDetection'
import { IFeatureToggle, IFeatureToggles } from '@src/types/featureToggles.types'
import trackingServiceApi from '@src/api/tracking'
import { IImpressions } from '@src/types/tracking.types'
import userApi from '@src/api/user'
import { IUnauthenticatedRealmSettings } from '@src/types/realmSettings.types'
import { IUser } from '@src/types/user.types'
import { getIsNewUserWithinDays } from '@src/pinia/user'

export async function loadAnalyticsDependencies() {
  // run request in parallel
  const [
    { data: knowledgeSpaces },
    { data: user },
    { data: roles },
    { data: realmSettings }
  ] = await Promise.all([
    usersApi.cached.getKnowledgeSpaces(),
    usersApi.cached.getCurrentUser(),
    usersApi.cached.getRoles(),
    realmSettingsApi.cached.get()
  ])

  const [
    { data: featureToggles },
    { data: impressionsData },
    { data: userActivity },
    { data: conceptSubscriptions },
    { data: conceptExperienceCount }
  ] = await Promise.all([
    unleashApi.cached.get(user.event_tracking_id, realmSettings.metadata.realm_id, user.language),
    trackingServiceApi.cached.getImpressions(user.event_tracking_id),
    statisticsApi.cached.getUserActivity(user.id),
    personalisationApi.cached.getConceptSubscriptions(user.global_user_id),
    ulfShieldExpertsApi.cached.getExperienceCount(user.global_user_id)
  ])

  const experimentsSuperProps = getExperimentSuperProps(featureToggles, impressionsData)

  const mergedProps: ISuperproperties = {
    approximated_expert_tags: conceptExperienceCount.concept_experience_count,
    network_id: realmSettings.metadata.realm_id,
    network_go_live_date: realmSettings?.metadata.go_live_date,
    user_tracking_id: user.event_tracking_id,
    new_user: getIsNewUserWithinDays(user.first_seen, 7),
    first_seen: user.first_seen,
    last_seen: user.last_seen,
    ui_language: user.language,
    permission_level: roles ? getPermissionLevel(roles) : PermissionLevel.User,
    user_type: realmSettings.metadata.is_trial ? 'trial' : 'paid',
    pricing_tier: realmSettings.metadata.pricing_tier,
    total_solutions_sent: userActivity.solutions,
    total_questions_sent: userActivity.questions,
    total_comments_sent: userActivity.comments,
    total_recommendations_sent: userActivity.recommendations_sent,
    total_recommendations_received: userActivity.recommendations_received,
    total_subscriptions: conceptSubscriptions.length,
    total_knowledge_spaces_of_user: knowledgeSpaces.total,
    nps_score: user.nps_score,
    solutions_received: userActivity.solutions_received,
    appType:
            _get(window, 'appWrapperContextVariables.appPlatform') || 'browser',
    appOs: _get(window, 'appWrapperContextVariables.appOs'),
    cross_domain_iframe_integration: isInCrossDomainIframe(),
    ...experimentsSuperProps
  }

  return {
    user,
    mergedProps,
    realmSettings
  }
}

function getExperimentSuperProps(featureToggles: IFeatureToggles, impressions: IImpressions) {
  return featureToggles.toggles
    .filter(({ variant: { enabled } }) => enabled)
    .reduce((acc: { [key: string]: string | boolean | null }, experiment: IFeatureToggle) => {
      const accCopy = { ...acc }
      accCopy[`${experiment.name}_variant`] = experiment.variant.payload?.value ?? null
      accCopy[`${experiment.name}_impression`] = impressions.includes(experiment.name)
      return accCopy
    }, {})
}

export async function initAskNicely(
  realmSettings: IUnauthenticatedRealmSettings,
  user: IUser,
  superproperties: ISuperproperties
) {
  if (!realmSettings?.nps.enabled) return

  try {
    // Dynamically load JS
    const script = document?.createElement('script')
    script.src = 'https://static.asknice.ly/dist/standalone/asknicely-in-app-conversation.js'
    script.async = true
    script.onload = async () => {
      if (!(window as any).askNicelyConversation) {
        return
      }

      const currentUser = user
      const networkName = realmSettings.metadata.realm_name
      const companyName = realmSettings.metadata.company_name
      const { data: { email_hash } } = await userApi.getAskNicelyEmailHash(user.id);

      (window as any).askNicelyConversation({
        config: {
          domain_key: 'starmind', // Required
          template_name: 'Default',
          name: `${currentUser.firstname} ${currentUser.lastname}`,
          email: currentUser.email,
          email_hash,
          created: Math.round((new Date(currentUser.first_seen ?? Date.now())).valueOf() / 1000),
          auto_close: true,
          delivery_method: 'web'
        },
        customData: {
          network_id: superproperties.network_id,
          network_name: networkName,
          language: currentUser.language,
          gender: currentUser.gender,
          total_questions_sent: superproperties.total_questions_sent,
          total_solutions_sent: superproperties.total_solutions_sent,
          total_comments_sent: superproperties.total_comments_sent,
          total_recommendations_sent: superproperties.total_recommendations_sent,
          total_recommendations_received: superproperties.total_recommendations_received,
          solutions_received: superproperties.solutions_received,
          event_tracking_id: superproperties.user_tracking_id,
          company_name: companyName,
          job_title: currentUser.position,
          user_segment: superproperties.user_segment,
          feature: superproperties.feature
        }
      })
    }

    document.body.appendChild(script)
  } catch (error) {
    // DO NOTHING
  }
}
